import React from "react";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";

const LABS_URL = "https://labs.katlastechnology.com/";
const PLATFORM_URL = "https://platform.katlastechnology.com/";

const STEPS = [
  {
    title: "Bring the stuck moment",
    body: "The decision, exception or handoff that currently depends on email, calls, shared folders or personal escalation.",
  },
  {
    title: "Map the authority journey",
    body: "Identify roles, evidence holders, decision boundaries, policy conditions and accountable outcomes.",
  },
  {
    title: "Design the governed path",
    body: "Explore a focused Cierge route: bounded request, minimum proof, authorised action and receipt.",
  },
];

// Forward-movement motif: a stalled moment resolving into a governed route.
function JourneyRibbon() {
  return (
    <svg className="bridge__ribbon" viewBox="0 0 1080 64" role="img"
         aria-label="A stalled moment becoming a governed route">
      <text x="22" y="18" className="rb-cap rb-cap--stall">A STALLED MOMENT</text>
      <text x="1058" y="18" textAnchor="end" className="rb-cap rb-cap--gov">A GOVERNED ROUTE</text>
      <line x1="28" y1="44" x2="556" y2="44" stroke="#A86A2E" strokeWidth="1.6" strokeDasharray="3 6" opacity="0.7" />
      <line x1="556" y1="44" x2="1052" y2="44" stroke="#2660F2" strokeWidth="2.4" />
      <circle cx="28" cy="44" r="7" fill="none" stroke="#A86A2E" strokeWidth="2" />
      <circle cx="28" cy="44" r="2.6" fill="#A86A2E" />
      {[180, 540, 900].map((x, i) => (
        <circle key={i} cx={x} cy="44" r="4" fill={x < 556 ? "#F3EFE6" : "#2660F2"}
                stroke={x < 556 ? "#A86A2E" : "#2660F2"} strokeWidth="1.6" />
      ))}
      <circle cx="1052" cy="44" r="11" fill="#2660F2" />
      <path d="M 1046 44 l 3.6 3.6 l 7 -8" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function BridgeToCierge() {
  return (
    <section className="section section--bridge" id="cierge" data-testid="bridge-section">
      <div className="wrap">
        <Reveal className="bridge">
          <div className="bridge__head">
            <span className="sec-kicker sec-kicker--blue">Next: apply the pattern</span>
            <h2 className="sec-title">Bring your difficult journey to Cierge Labs.</h2>
            <p className="bridge__body">
              Your problem may not be a consignment release. It may be a patient handover, AI
              escalation, supplier exception, infrastructure approval, funding decision or
              regulatory route.
            </p>
            <p className="bridge__body">
              Cierge Labs is KATLAS Technology’s applied journey, discovery and pilot studio. It is
              where a real coordination problem becomes visible: who holds the evidence, who may
              act, what proof is needed, where the handoff fails, and what a governed route could
              look like.
            </p>
          </div>

          <JourneyRibbon />

          <div className="bridge__cards">
            {STEPS.map((s, i) => (
              <React.Fragment key={i}>
                <div className="bcard" data-testid={"bcard-" + i}>
                  <span className="bcard__n">{i + 1}</span>
                  <h3 className="bcard__title">{s.title}</h3>
                  <p className="bcard__body">{s.body}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <span className="bcard__sep" aria-hidden="true"><ArrowRight size={18} /></span>
                )}
              </React.Fragment>
            ))}
          </div>

          <p className="bridge__close">
            The Authority Journey shows the pattern. Cierge Labs helps you apply it to your world.
          </p>

          <div className="bridge__cta">
            <a className="btn btn--accent" href={LABS_URL} data-testid="bridge-cta-primary">
              Explore Cierge Labs <ArrowRight size={16} />
            </a>
            <a className="bridge__textlink" href={PLATFORM_URL} data-testid="bridge-cta-secondary">
              Return to the KATLAS Platform <ArrowRight size={14} />
            </a>
          </div>

          <div className="bridge__studio" data-testid="bridge-studio-label">
            Cierge Labs · Applied journey, discovery and pilot studio
          </div>
        </Reveal>
      </div>
    </section>
  );
}
